// DownloadLogsModal.jsx - Modal de logs de download por empresa.
// Reescrito em 2 abas:
//   - "Visão geral": recorte FORÇADO no mês atual (por competência) — total baixado,
//     quebra Tomados × Prestados e chaves com erro. Card de progresso ao vivo da coleta.
//   - "Notas": tabela paginada com filtro livre (NotesTable).
// Fonte de dados: GET /downloads/logs/:companyId (filtra por competence_date quando
// recebe date_from/date_to). Nenhuma mudança no backend.

import { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import { Stack, HStack } from "@astryxdesign/core/Stack";
import { Grid } from "@astryxdesign/core/Grid";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Card } from "@astryxdesign/core/Card";
import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Banner } from "@astryxdesign/core/Banner";
import { Spinner } from "@astryxdesign/core/Spinner";
import { ProgressBar } from "@astryxdesign/core/ProgressBar";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { TabList, Tab } from "@astryxdesign/core/TabList";
import { downloadLogsService } from "../../services/downloadLogsService";
import { isFinishedJobStatus } from "../../utils/downloadStatusUtils";
import NotesTable from "./NotesTable";
import IndividualJobTrigger from "./IndividualJobTrigger";

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Quantos dias tem o mês (monthIndex 0-based: 0 = janeiro ... 11 = dezembro).
 * O truque `new Date(year, month + 1, 0)` = último dia do mês → 28/29/30/31.
 */
const getDaysInMonth = (year, monthIndex) =>
  new Date(year, monthIndex + 1, 0).getDate();

/**
 * Intervalo de um mês (YYYY-MM-DD) + rótulo de competência MM/AAAA.
 * A data final usa o número de dias do mês, então o período cobre o mês
 * inteiro: 01/07/2026 → 31/07/2026, 01/06/2026 → 30/06/2026, etc.
 * O log é recortado neste período (por competência).
 */
const getMonthRange = (year, monthIndex) => {
  const lastDay = getDaysInMonth(year, monthIndex);
  const first = `${year}-${pad2(monthIndex + 1)}-01`;
  const last = `${year}-${pad2(monthIndex + 1)}-${pad2(lastDay)}`;
  return { first, last, label: `${pad2(monthIndex + 1)}/${year}` };
};

/** Ano/mês (0-based) do mês corrente. */
const getCurrentYearMonth = () => {
  const now = new Date();
  return { year: now.getFullYear(), monthIndex: now.getMonth() };
};

/** Badge do certificado digital da empresa (habilita/indica a coleta via ADN). */
const certBadge = (certStatus) => {
  switch (certStatus) {
    case "valid":
      return <Badge variant="success" label="Certificado válido" />;
    case "expiring":
      return <Badge variant="warning" label="Certificado a vencer" />;
    case "expired":
      return <Badge variant="error" label="Certificado vencido" />;
    default:
      return <Badge variant="neutral" label="Sem certificado" />;
  }
};

const noteTypeBadge = (noteType) => {
  if (noteType === "prestados")
    return <Badge variant="info" label="Prestados" />;
  if (noteType === "tomados")
    return <Badge variant="purple" label="Tomados" />;
  return <Badge variant="neutral" label="Desconhecido" />;
};

/**
 * DownloadLogsModal
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Object} props.company - empresa (id, name, cnpj, cert_status)
 */
const DownloadLogsModal = ({ isOpen, onClose, company }) => {
  // Competência selecionada (ano + mês 0-based). Default = mês corrente.
  const current = useMemo(getCurrentYearMonth, []);
  const [selected, setSelected] = useState(current);
  const period = useMemo(
    () => getMonthRange(selected.year, selected.monthIndex),
    [selected],
  );
  const isCurrentMonth =
    selected.year === current.year &&
    selected.monthIndex === current.monthIndex;

  // Avança/retrocede meses (não permite passar do mês corrente).
  const shiftMonth = (delta) => {
    setSelected((s) => {
      const d = new Date(s.year, s.monthIndex + delta, 1);
      return { year: d.getFullYear(), monthIndex: d.getMonth() };
    });
  };
  const goToCurrentMonth = () => setSelected(current);

  const [activeTab, setActiveTab] = useState("overview");
  const [logsData, setLogsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [currentJobProgress, setCurrentJobProgress] = useState(null);

  // Comparação Hub × Portal Nacional (por processamento/dhProc) — card do mês
  // atual. O portal filtra pela data de processamento da NFSe, não pela de
  // emissão do DPS (que o emissor retroage), então o Hub é contado igual.
  const [portalCmp, setPortalCmp] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [portalRefreshing, setPortalRefreshing] = useState(false);

  /** Carrega o resumo do log recortado no mês atual (por competência). */
  const loadLogsData = useCallback(async () => {
    if (!company?.id) return;
    try {
      setLoading(true);
      setError(null);

      const response = await downloadLogsService.getCompanyDownloadLogs(
        company.id,
        { date_from: period.first, date_to: period.last, limit: 50 },
      );

      if (response.success) {
        setLogsData(response.data);
        setLastUpdated(new Date());
      } else {
        setError(response.message || "Erro ao carregar logs de download");
      }
    } catch (err) {
      console.error("Error loading logs data:", err);
      setError("Erro interno ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [company?.id, period.first, period.last]);

  /** Carrega a comparação Hub × Portal (por processamento) do mês atual. */
  const loadPortalComparison = useCallback(async () => {
    if (!company?.id) return;
    try {
      setPortalLoading(true);
      setPortalError(null);
      const response = await downloadLogsService.getPortalComparison(
        company.id,
        { date_from: period.first, date_to: period.last },
      );
      if (response.success) {
        setPortalCmp(response.data);
      } else {
        setPortalError(response.message || "Erro ao comparar com o portal");
      }
    } catch (err) {
      console.error("Error loading portal comparison:", err);
      setPortalError("Erro interno ao comparar com o portal");
    } finally {
      setPortalLoading(false);
    }
  }, [company?.id, period.first, period.last]);

  /** Dispara a contagem ao vivo do portal e recarrega a comparação. */
  const handleRefreshPortalCount = async () => {
    if (!company?.id) return;
    try {
      setPortalRefreshing(true);
      setPortalError(null);
      const response = await downloadLogsService.refreshPortalCount(
        company.id,
        { date_from: period.first, date_to: period.last },
      );
      if (response.success) {
        await loadPortalComparison();
      } else {
        setPortalError(
          response.message || "Não foi possível atualizar a contagem do portal",
        );
      }
    } catch (err) {
      console.error("Error refreshing portal count:", err);
      setPortalError("Erro interno ao atualizar a contagem do portal");
    } finally {
      setPortalRefreshing(false);
    }
  };

  const refreshData = () => {
    loadLogsData();
    loadPortalComparison();
  };

  /** Coleta iniciada: registra job ativo e mostra o card de progresso. */
  const handleJobStarted = (jobId) => {
    setActiveJobs((prev) => [...prev, jobId]);
    setCurrentJobProgress({
      jobId,
      status: "pending",
      totalNotes: 0,
      downloadedNotes: 0,
      percentage: 0,
    });
    setTimeout(() => {
      loadLogsData();
    }, 1000);
  };

  /**
   * Atualização de progresso vinda do polling (card "Baixando XMLs").
   * O percentual nunca retrocede (evita a barra andar pra trás ao trocar
   * Tomados → Prestados).
   */
  const handleJobProgressUpdate = useCallback((job) => {
    const status = job?.status;
    if (isFinishedJobStatus(status)) {
      setCurrentJobProgress(null);
      return;
    }
    const totalNotes =
      job?.total_notes_found ?? job?.total_notes_downloaded ?? 0;
    const downloadedNotes = job?.total_notes_downloaded ?? 0;
    const rawPercentage =
      job?.progress_percentage != null
        ? job.progress_percentage
        : totalNotes
          ? Math.round((downloadedNotes / totalNotes) * 100)
          : 0;
    setCurrentJobProgress((prev) => {
      const percentage =
        prev?.percentage != null
          ? Math.max(prev.percentage, rawPercentage)
          : rawPercentage;
      return {
        jobId: job?.id,
        status: status || "pending",
        totalNotes,
        downloadedNotes,
        percentage,
      };
    });
  }, []);

  const formatLastUpdated = () => {
    if (!lastUpdated) return null;
    return downloadLogsService.formatDateForDisplay(lastUpdated.toISOString());
  };

  // Carrega ao abrir
  useEffect(() => {
    if (isOpen && company?.id) {
      loadLogsData();
      loadPortalComparison();
    }
  }, [isOpen, company?.id, loadLogsData, loadPortalComparison]);

  // Reseta ao fechar
  useEffect(() => {
    if (!isOpen) {
      setActiveTab("overview");
      setLogsData(null);
      setError(null);
      setLastUpdated(null);
      setActiveJobs([]);
      setCurrentJobProgress(null);
      setPortalCmp(null);
      setPortalError(null);
      setSelected(current);
    }
  }, [isOpen, current]);

  // Remove jobs já finalizados da lista de ativos
  useEffect(() => {
    if (activeJobs.length === 0 || !logsData?.recent_jobs) return;
    const completedJobIds = logsData.recent_jobs
      .filter((job) => isFinishedJobStatus(job.status))
      .map((job) => job.id);
    if (completedJobIds.length > 0) {
      setActiveJobs((prev) =>
        prev.filter((jobId) => !completedJobIds.includes(jobId)),
      );
    }
  }, [logsData?.recent_jobs, activeJobs]);

  // Garante que o card de progresso some quando o job termina,
  // mesmo se a última callback do polling se perder.
  useEffect(() => {
    if (!currentJobProgress || !logsData?.recent_jobs) return;
    const matchingJob = logsData.recent_jobs.find(
      (job) => job.id === currentJobProgress.jobId,
    );
    if (matchingJob && isFinishedJobStatus(matchingJob.status)) {
      setCurrentJobProgress(null);
    }
  }, [currentJobProgress, logsData?.recent_jobs]);

  if (!isOpen) return null;

  const stats = logsData?.statistics;
  const byType = stats?.byType;
  const failedNotes = stats?.failedNotes || [];
  const hasNotesInPeriod = (stats?.total || 0) > 0;
  const showProgress =
    currentJobProgress &&
    (currentJobProgress.status === "pending" ||
      currentJobProgress.status === "running");

  // Bloco de quebra por tipo (Tomados / Prestados)
  const typeCard = (title, icon, data) => (
    <Card padding={4}>
      <Stack gap={2}>
        <HStack gap={2} vAlign="center">
          <Text color="accent">
            <FontAwesomeIcon icon={icon} />
          </Text>
          <Text weight="semibold">{title}</Text>
        </HStack>
        <Stack gap={1}>
          <HStack gap={2} hAlign="between" vAlign="center">
            <Text type="supporting">Total no mês</Text>
            <Text size="sm" weight="medium">
              {data?.total || 0}
            </Text>
          </HStack>
          <HStack gap={2} hAlign="between" vAlign="center">
            <HStack gap={1} vAlign="center">
              <StatusDot variant="success" label="Baixadas" />
              <Text type="supporting">Baixadas</Text>
            </HStack>
            <Text size="sm" weight="medium">
              {data?.success || 0}
            </Text>
          </HStack>
          <HStack gap={2} hAlign="between" vAlign="center">
            <HStack gap={1} vAlign="center">
              <StatusDot variant="error" label="Sem arquivo" />
              <Text type="supporting">Sem arquivo</Text>
            </HStack>
            <Text size="sm" weight="medium">
              {data?.failed || 0}
            </Text>
          </HStack>
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width={1100}
      maxHeight="92vh"
    >
      <Layout
        header={
          <DialogHeader
            title="Logs de Download"
            subtitle={company ? `${company.name} • CNPJ: ${company.cnpj}` : ""}
            onOpenChange={(open) => {
              if (!open) onClose();
            }}
            hasDivider
          />
        }
        content={
          <LayoutContent>
            <Stack gap={3}>
              {/* Loading inicial */}
        {loading && !logsData && (
          <HStack gap={2} vAlign="center" hAlign="center">
            <Spinner size="lg" label="Carregando logs" />
            <Stack gap={0.5}>
              <Text>Carregando logs de download...</Text>
              <Text type="supporting">
                Buscando dados da empresa {company?.name}
              </Text>
            </Stack>
          </HStack>
        )}

        {/* Erro */}
        {error && (
          <Stack gap={2}>
            <Banner
              status="error"
              title="Erro ao Carregar Logs"
              description={error}
            />
            <HStack gap={2}>
              <Button
                label="Tentar Novamente"
                variant="secondary"
                icon={<FontAwesomeIcon icon="sync" />}
                onClick={refreshData}
              />
            </HStack>
          </Stack>
        )}

        {/* Conteúdo */}
        {logsData && !loading && (
          <Stack gap={3}>
            {/* Cabeçalho comum: certificado + disparo de coleta */}
            <HStack gap={2} vAlign="center" hAlign="between" wrap="wrap">
              <HStack gap={2} vAlign="center" wrap="wrap">
                <Text type="supporting">Coleta:</Text>
                {certBadge(company?.cert_status)}
              </HStack>
            </HStack>

            <IndividualJobTrigger
              company={company}
              onJobStarted={handleJobStarted}
              onRefresh={refreshData}
              onJobProgressUpdate={handleJobProgressUpdate}
            />

            {/* Card de progresso ao vivo */}
            {showProgress && (
              <Card padding={4}>
                <Stack gap={2}>
                  <HStack gap={2} vAlign="center">
                    {currentJobProgress.status === "running" ? (
                      <Spinner size="sm" label="Baixando" />
                    ) : (
                      <Text color="secondary">
                        <FontAwesomeIcon icon="download" />
                      </Text>
                    )}
                    <Text weight="semibold">Baixando XMLs</Text>
                    {company?.name && (
                      <Text type="supporting">{company.name}</Text>
                    )}
                  </HStack>
                  <ProgressBar
                    label="Progresso da coleta"
                    isLabelHidden
                    value={Math.min(currentJobProgress.percentage ?? 0, 100)}
                    max={100}
                  />
                  <HStack gap={2} hAlign="between">
                    <Text type="supporting">
                      {currentJobProgress.totalNotes === 0
                        ? "Identificando notas a baixar..."
                        : `${currentJobProgress.downloadedNotes} de ${currentJobProgress.totalNotes}`}
                    </Text>
                    <Text type="supporting">
                      {currentJobProgress.totalNotes === 0
                        ? "—"
                        : `${currentJobProgress.percentage ?? 0}%`}
                    </Text>
                  </HStack>
                </Stack>
              </Card>
            )}

            {/* Abas */}
            <TabList
              label="Seções do log"
              value={activeTab}
              onChange={setActiveTab}
              hasDivider
            >
              <Tab
                value="overview"
                label="Visão geral"
                icon={<FontAwesomeIcon icon="chart-pie" />}
              />
              <Tab
                value="notes"
                label="Notas"
                icon={<FontAwesomeIcon icon="file-alt" />}
              />
            </TabList>

            {/* Aba: Visão geral (mês atual, por competência) */}
            {activeTab === "overview" && (
              <Stack gap={4}>
                <HStack gap={2} vAlign="center" wrap="wrap">
                  <Text color="accent">
                    <FontAwesomeIcon icon="calendar-alt" />
                  </Text>
                  <Text weight="semibold">Competência</Text>
                  <IconButton
                    label="Mês anterior"
                    variant="ghost"
                    size="sm"
                    icon={<FontAwesomeIcon icon="chevron-left" />}
                    isDisabled={loading || portalLoading}
                    onClick={() => shiftMonth(-1)}
                  />
                  <Badge variant="info" label={period.label} />
                  <IconButton
                    label="Próximo mês"
                    variant="ghost"
                    size="sm"
                    icon={<FontAwesomeIcon icon="chevron-right" />}
                    isDisabled={isCurrentMonth || loading || portalLoading}
                    onClick={() => shiftMonth(1)}
                  />
                  {!isCurrentMonth && (
                    <Button
                      label="Mês atual"
                      variant="ghost"
                      size="sm"
                      isDisabled={loading || portalLoading}
                      onClick={goToCurrentMonth}
                    />
                  )}
                </HStack>

                {hasNotesInPeriod ? (
                  <>
                    {/* KPIs */}
                    <Grid columns={{ minWidth: 200, max: 3 }} gap={3}>
                      <Card padding={4}>
                        <Stack gap={0.5}>
                          <Text type="label" color="secondary">
                            Total no período
                          </Text>
                          <Heading level={3} type="display-2">
                            {stats?.total || 0}
                          </Heading>
                          <Text type="supporting">
                            Notas na competência {period.label}
                          </Text>
                        </Stack>
                      </Card>
                      <Card padding={4}>
                        <Stack gap={0.5}>
                          <Text type="label" color="secondary">
                            Baixadas
                          </Text>
                          <Heading level={3} type="display-2">
                            {stats?.success || 0}
                          </Heading>
                          <Badge variant="success" label="Com arquivo (XML)" />
                        </Stack>
                      </Card>
                      <Card padding={4}>
                        <Stack gap={0.5}>
                          <Text type="label" color="secondary">
                            Sem arquivo
                          </Text>
                          <Heading level={3} type="display-2">
                            {stats?.failed || 0}
                          </Heading>
                          <Badge variant="error" label="Faltando baixar" />
                        </Stack>
                      </Card>
                    </Grid>

                    {/* Tomados × Prestados */}
                    <Grid columns={{ minWidth: 240, max: 2 }} gap={3}>
                      {typeCard(
                        "Serviços Prestados",
                        "file-invoice",
                        byType?.prestados,
                      )}
                      {typeCard(
                        "Serviços Tomados",
                        "file-invoice-dollar",
                        byType?.tomados,
                      )}
                    </Grid>

                    {/* Chaves com erro */}
                    {failedNotes.length > 0 && (
                      <Card padding={4}>
                        <Stack gap={2}>
                          <Text weight="semibold">
                            Chaves de acesso com falha no download (
                            {failedNotes.length})
                          </Text>
                          <Stack
                            gap={2}
                            isScrollable
                            height={failedNotes.length > 4 ? 240 : undefined}
                          >
                            {failedNotes.map((note, index) => (
                              <Card key={index} padding={2}>
                                <HStack
                                  gap={2}
                                  vAlign="center"
                                  hAlign="between"
                                >
                                  <Stack gap={0.5}>
                                    <Text size="sm">
                                      {note.accessKey || "N/A"}
                                    </Text>
                                    {note.error && (
                                      <Text type="supporting">
                                        Erro: {note.error}
                                      </Text>
                                    )}
                                  </Stack>
                                  {noteTypeBadge(note.noteType)}
                                </HStack>
                              </Card>
                            ))}
                          </Stack>
                        </Stack>
                      </Card>
                    )}
                  </>
                ) : (
                  <EmptyState
                    title="Nenhuma nota na competência do mês atual"
                    description={`Não há notas com competência em ${period.label} para esta empresa. Use a coleta acima ou consulte outros períodos na aba "Notas".`}
                    icon={<FontAwesomeIcon icon="calendar-times" />}
                  />
                )}

                {/* Comparação Hub × Portal Nacional (por processamento) */}
                <Card padding={4}>
                  <Stack gap={3}>
                    <HStack gap={2} vAlign="center" hAlign="between" wrap="wrap">
                      <HStack gap={2} vAlign="center" wrap="wrap">
                        <Text color="accent">
                          <FontAwesomeIcon icon="chart-bar" />
                        </Text>
                        <Text weight="semibold">Portal Nacional</Text>
                        <Badge
                          variant="neutral"
                          label={`Por processamento · ${period.label}`}
                        />
                      </HStack>
                      <Button
                        label={
                          portalRefreshing
                            ? "Consultando portal..."
                            : "Atualizar contagem do portal"
                        }
                        variant="secondary"
                        icon={<FontAwesomeIcon icon="sync" />}
                        isLoading={portalRefreshing}
                        isDisabled={portalLoading || portalRefreshing}
                        onClick={handleRefreshPortalCount}
                      />
                    </HStack>

                    {portalError && (
                      <Banner status="error" title={portalError} />
                    )}

                    {portalLoading && !portalCmp ? (
                      <HStack gap={2} vAlign="center">
                        <Spinner size="sm" label="Carregando comparação" />
                        <Text type="supporting">
                          Carregando comparação com o portal...
                        </Text>
                      </HStack>
                    ) : (
                      <>
                        <Grid columns={{ minWidth: 200, max: 3 }} gap={3}>
                          {/* Hub (por processamento) */}
                          <Card padding={3}>
                            <Stack gap={1}>
                              <Text type="label" color="secondary">
                                Hub (baixadas)
                              </Text>
                              <Heading level={4} type="display-2">
                                {portalCmp?.hub?.total ?? 0}
                              </Heading>
                              <Text type="supporting">
                                Tomados {portalCmp?.hub?.tomados ?? 0} ·
                                Prestados {portalCmp?.hub?.prestados ?? 0}
                              </Text>
                            </Stack>
                          </Card>

                          {/* Portal */}
                          <Card padding={3}>
                            <Stack gap={1}>
                              <Text type="label" color="secondary">
                                Portal Nacional
                              </Text>
                              <Heading level={4} type="display-2">
                                {portalCmp?.portal?.total ?? "—"}
                              </Heading>
                              {portalCmp?.portal ? (
                                <Text type="supporting">
                                  Tomados {portalCmp.portal.tomados ?? "—"} ·
                                  Prestados {portalCmp.portal.prestados ?? "—"}
                                </Text>
                              ) : (
                                <Text type="supporting">
                                  Sem contagem registrada
                                </Text>
                              )}
                            </Stack>
                          </Card>

                          {/* Diferença */}
                          <Card padding={3}>
                            <Stack gap={1}>
                              <Text type="label" color="secondary">
                                Diferença (Portal − Hub)
                              </Text>
                              <Heading level={4} type="display-2">
                                {portalCmp?.difference?.total != null
                                  ? portalCmp.difference.total
                                  : "—"}
                              </Heading>
                              <Text type="supporting">
                                {portalCmp?.difference?.total == null
                                  ? "Depende da contagem do portal"
                                  : portalCmp.difference.total > 0
                                    ? "Faltam baixar no Hub"
                                    : portalCmp.difference.total < 0
                                      ? "Hub tem mais que o portal"
                                      : "Hub e portal batem"}
                              </Text>
                            </Stack>
                          </Card>
                        </Grid>

                        {portalCmp?.portal ? (
                          portalCmp.difference?.total > 0 ? (
                            <Banner
                              status="warning"
                              title={`Faltam baixar ${portalCmp.difference.total} nota(s)`}
                              description={`O portal lista mais notas que o Hub no processamento de ${period.label}.${
                                portalCmp.portal.asOf
                                  ? ` Contagem do portal de ${downloadLogsService.formatDateForDisplay(
                                      portalCmp.portal.asOf,
                                    )}.`
                                  : ""
                              }`}
                            />
                          ) : (
                            <Banner
                              status="success"
                              title="Hub em dia com o portal"
                              description={`Nenhuma nota faltando no processamento de ${period.label}.${
                                portalCmp.portal.asOf
                                  ? ` Contagem do portal de ${downloadLogsService.formatDateForDisplay(
                                      portalCmp.portal.asOf,
                                    )}.`
                                  : ""
                              }`}
                            />
                          )
                        ) : (
                          <Banner
                            status="info"
                            title="Sem contagem do portal para este período"
                            description='Clique em "Atualizar contagem do portal" para consultar o total do Portal Nacional agora (login + leitura do rodapé).'
                          />
                        )}
                      </>
                    )}
                  </Stack>
                </Card>
              </Stack>
            )}

            {/* Aba: Notas (filtro livre) */}
            {activeTab === "notes" && (
              <NotesTable
                companyId={company.id}
                initialStartDate={period.first}
                initialEndDate={period.last}
              />
            )}
          </Stack>
        )}

            </Stack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter hasDivider>
            <HStack gap={2} vAlign="center" hAlign="between" wrap="wrap">
              <Text type="supporting">
                {logsData ? `Dados carregados em ${formatLastUpdated()}` : ""}
              </Text>
              <HStack gap={2}>
                <Button
                  label={loading ? "Atualizando..." : "Atualizar"}
                  variant="secondary"
                  icon={<FontAwesomeIcon icon="sync" />}
                  isLoading={loading}
                  onClick={refreshData}
                />
                <Button label="Fechar" variant="ghost" onClick={onClose} />
              </HStack>
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
};

export default DownloadLogsModal;
